import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { EmptyState, LoadingState, ErrorState } from "../../core/common/ui/PagePrimitives";
import PropTypes from "prop-types";
import { all_routes } from "../../Router/all_routes";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

// redux
import { setCurrent_location } from "../../core/redux/action";

// MUI
import Avatar from "@mui/material/Avatar";

// utils
import utils from "../../utils/utils";
import { canMakeSales } from "../../utils/roles";
import apiClient from "../../utils/apiClient";

const Header = ({ onToggleNavigation, navigationExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user_infos = useSelector((state) => state.rootReducer.user_infos);
  const user_location = useSelector((state) => state.rootReducer.user_location);
  const current_location = useSelector((state) => state.rootReducer.current_location);
  const route = all_routes;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationError, setNotificationError] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { stringAvatar } = utils;
  const dispatch = useDispatch();
  const role = user_infos?.role;

  useEffect(() => {
    const isEmptyObject =
      typeof current_location === "object" &&
      current_location !== null &&
      Object.keys(current_location).length === 0;

    if ((current_location == null || isEmptyObject) && user_location?.length) {
      dispatch(setCurrent_location(user_location[0]));
    }
  }, [current_location, user_location, dispatch]);

  const formatNotificationDate = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_) {
      return "";
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!user_infos?.establishmentId) return;
    setNotificationsLoading(true);
    setNotificationError(false);
    try {
      const { data } = await apiClient.get(
        `/establishments/${user_infos.establishmentId}/notifications`,
        { params: { limit: 15 } }
      );
      setNotifications(data || []);
    } catch (error) {
      setNotificationError(true);
      console.error("Error fetching notifications:", error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user_infos?.establishmentId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    if (!user_infos?.establishmentId) return;
    try {
      await apiClient.post("/notifications/mark-all-read", {
        establishmentId: user_infos.establishmentId
      });
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, isRead: true }))
      );
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const notificationTitle = (notification) => {
    if (notification?.title) return notification.title;
    switch (notification?.type) {
      case "birthday":
        return t("birthday reminder title");
      case "loyalty":
        return t("loyalty milestone title");
      default:
        return t("notifications");
    }
  };

  const notificationMessage = (notification) => {
    switch (notification?.type) {
      case "birthday":
        return t("birthday reminder body", {
          name: notification?.customerName || "",
          date: formatNotificationDate(notification?.eventDate || notification?.metadata?.birthdate),
        });
      case "loyalty":
        return t("loyalty milestone body", {
          name: notification?.customerName || "",
          points: notification?.milestone || notification?.metadata?.points || 0,
        });
      default:
        return notification?.message || "";
    }
  };

  const notificationTarget = useCallback((notification) => {
    const customerId = String(notification?.customerId || "").trim();
    if (!customerId) return route.customer;

    const params = new URLSearchParams();
    params.set("openCustomerId", customerId);
    if (notification?.customerName) {
      params.set("customerName", String(notification.customerName));
    }

    return `${route.customer}?${params.toString()}`;
  }, [route.customer]);

  const handleNotificationClick = useCallback((notification, event) => {
    const target = notificationTarget(notification);
    const notificationId = notification?._id || notification?.id;

    if (!target) {
      event?.preventDefault();
      return;
    }

    if (notificationId) {
      apiClient.post(`/notifications/${notificationId}/read`).catch((error) => {
        console.error("Failed to mark notification as read:", error);
      });

      setNotifications((prev) =>
        prev.filter((item) => (item?._id || item?.id) !== notificationId)
      );
    }

    if (location.pathname === route.customer && location.search === target.replace(route.customer, "")) {
      event?.preventDefault();
      navigate(target, { replace: true });
      return;
    }
  }, [location.pathname, location.search, navigate, notificationTarget, route.customer]);

  const handleRole = (role) => {
    switch (role) {
      case "su":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "salon_manager":
        return lang === "fr" ? "Caissière" : "Cashier";
      case "manager":
        return "Manager";
      case "logistic":
        return lang === "fr" ? "Logistique" : "Logistics";
      case "compta":
        return lang === "fr" ? "Comptable" : "Accountant";
      case "rh":
        return lang === "fr" ? "RH" : "HR";
      case "com":
        return lang === "fr" ? "Commercial" : "Sales";
      default:
        return lang === "fr" ? "Inconnu" : "Unknown";
    }
  }

  const changeLanguage = lng => i18n.changeLanguage(lng);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  // Call this from a click handler (needs a user gesture for most browsers)
  function toggleFullscreen(elem) {
    var doc = document;
    elem = elem || doc.documentElement;

    var inIframe = (function () {
      try { return window.self !== window.top; } catch (_) { return true; }
    })();

    var isFull = doc.fullscreenElement || doc.webkitFullscreenElement ||
      doc.mozFullScreenElement || doc.msFullscreenElement;

    function request(el) {
      if (el.requestFullscreen) return el.requestFullscreen();
      if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
      if (el.mozRequestFullScreen) return el.mozRequestFullScreen();
      if (el.msRequestFullscreen) return el.msRequestFullscreen();
      return Promise.reject(new Error('Fullscreen API not available'));
    }

    function exit() {
      if (doc.exitFullscreen) return doc.exitFullscreen();
      if (doc.webkitExitFullscreen) return doc.webkitExitFullscreen();
      if (doc.mozCancelFullScreen) return doc.mozCancelFullScreen();
      if (doc.msExitFullscreen) return doc.msExitFullscreen();
      return Promise.reject(new Error('Exit Fullscreen API not available'));
    }

    if (!isFull) {
      var p = request(elem);
      if (inIframe) {
        // If the browser blocks child fullscreen, ask the parent to fullscreen the iframe.
        (p && typeof p.catch === 'function' ? p.catch.bind(p) : function (f) { f(); })(function () {
          window.parent.postMessage({ type: 'POS_TOGGLE_FULLSCREEN', action: 'enter' }, '*');
        });
      }
    } else {
      var q = exit();
      if (inIframe) {
        (q && typeof q.catch === 'function' ? q.catch.bind(q) : function (f) { f(); })(function () {
          window.parent.postMessage({ type: 'POS_TOGGLE_FULLSCREEN', action: 'exit' }, '*');
        });
      }
    }
  }

  return <header className="ui-topbar">
    <button type="button" className="ui-icon-button" onClick={onToggleNavigation}
      aria-label={t('ui.toggleNavigation')} aria-expanded={navigationExpanded}>
      <i className="ti ti-menu-2" aria-hidden="true" />
    </button>
    <Dropdown className="ui-location-menu">
      <Dropdown.Toggle variant="light" className="ui-location-toggle" id="workspace-menu">
        <span className="ui-location-icon"><i className="ti ti-building-store" aria-hidden="true" /></span>
        <span><small>{t('ui.workspace')}</small><strong>{current_location?.name || t('ui.chooseLocation')}</strong></span>
      </Dropdown.Toggle>
      <Dropdown.Menu>{user_location.map(item => <Dropdown.Item key={item._id} active={item._id === current_location?._id}
        onClick={() => dispatch(setCurrent_location(item))}>{item.name}</Dropdown.Item>)}</Dropdown.Menu>
    </Dropdown>
    <div className="ui-topbar-actions">
      {canMakeSales(role) && <Link to={route.pos} className="btn btn-primary ui-checkout-link"><i className="ti ti-shopping-cart" aria-hidden="true" />{t('ui.checkout')}</Link>}
      <Dropdown>
        <Dropdown.Toggle variant="light" className="ui-language-toggle" id="language-menu" aria-label={t('ui.language')}>{lang.startsWith('fr') ? 'FR' : 'EN'}</Dropdown.Toggle>
        <Dropdown.Menu align="end"><Dropdown.Item active={lang.startsWith('fr')} onClick={() => changeLanguage('fr')}>Français</Dropdown.Item><Dropdown.Item active={lang.startsWith('en')} onClick={() => changeLanguage('en')}>English</Dropdown.Item></Dropdown.Menu>
      </Dropdown>
      <button type="button" className="ui-icon-button ui-fullscreen" aria-label={t('ui.fullscreen')} onClick={() => toggleFullscreen()}><i className={`ti ti-${isFullscreen ? 'minimize' : 'maximize'}`} aria-hidden="true" /></button>
      <Dropdown>
        <Dropdown.Toggle variant="light" className="ui-icon-button ui-notification-toggle" id="notification-menu" aria-label={t('notifications')}>
          <i className="ti ti-bell" aria-hidden="true" />{unreadCount > 0 && <span className="ui-unread-count">{unreadCount}</span>}
        </Dropdown.Toggle>
        <Dropdown.Menu align="end" className="ui-notifications">
          <div className="ui-notifications-heading"><h2>{t('notifications')}</h2><button type="button" className="btn btn-link" onClick={handleMarkAllRead} disabled={notificationsLoading || !notifications.length}>{t('mark all as read')}</button></div>
          {notificationError ? <ErrorState message={t('ui.loadError')} onRetry={fetchNotifications} /> : notificationsLoading ? <LoadingState /> : !notifications.length ? <EmptyState title={t('no notifications')} icon="bell-check" /> :
            <div className="ui-notification-list">{notifications.map((item, index) => <Dropdown.Item as={Link} to={notificationTarget(item)} key={item._id || item.id || index}
              onClick={event => handleNotificationClick(item, event)} className={item.isRead ? '' : 'is-unread'}>
              <strong>{notificationTitle(item)}</strong><span>{notificationMessage(item)}</span><small>{formatNotificationDate(item.createdAt)}</small>
            </Dropdown.Item>)}</div>}
          <div className="ui-notifications-footer"><button type="button" className="btn btn-outline-secondary" onClick={fetchNotifications}>{t('refresh')}</button></div>
        </Dropdown.Menu>
      </Dropdown>
      <Dropdown>
        <Dropdown.Toggle variant="light" className="ui-profile-toggle" id="account-menu" aria-label={t('myprofile')}>
          <Avatar {...stringAvatar(user_infos?.name || '')} /><span className="ui-profile-name">{user_infos?.name}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end"><Dropdown.Header>{user_infos?.name}<br />{handleRole(role)}</Dropdown.Header>
          <Dropdown.Item as={Link} to={route.profile}>{t('myprofile')}</Dropdown.Item>
          <Dropdown.Divider /><Dropdown.Item as={Link} to={route.signin} onClick={() => sessionStorage.removeItem('token')}>{t('logout')}</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  </header>;
};
Header.propTypes = { onToggleNavigation: PropTypes.func.isRequired, navigationExpanded: PropTypes.bool };
export default Header;
